/* eslint-disable react-hooks/exhaustive-deps */
import { AutocompleteChangeDetails, AutocompleteChangeReason, AutocompleteOwnerState, AutocompleteRenderInputParams, AutocompleteRenderOptionState, FilterOptionsState, SxProps, Theme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { TextField } from 'src/components';
import Autocomplete from 'src/components/Autocomplete';
import VwTestDetaybilgiDTO from 'src/models/GenelTanimlar/VwTestDetaybilgiDTO';
import { AutoCompleteTargetNumber } from '../Types/autocomplete.target';
import { useGetTestDetayBilgiQuery } from 'src/services/Islem/TestDetayBilgiApi';
import { helper } from 'src/utils/helper';

interface ITestDetayBilgiAutocompleteProps {
    value?: number;
    disableClearable?: boolean;
    sx?: SxProps<Theme>;
    name?: string;
    label?: string;
    error?: boolean;
    children?: React.ReactNode;
    id?: string;
    required?: boolean;
    onChange?: (e: AutoCompleteTargetNumber) => void;
    helperText?: string;
    readonly?: boolean;
    disabled?: boolean;
    islemTanimId?: number;
    aktifGoster?: boolean;
};

/**
 * TestDetayBilgi autocomplete componenti
 * @param props 
 * @returns 
*/
const TestDetayBilgiAutocomplete = (props: ITestDetayBilgiAutocompleteProps): JSX.Element => {
    const query = useGetTestDetayBilgiQuery({
        id: props.islemTanimId,
        aktifGoster: props.aktifGoster ?? true
    });
    const [value, setValue] = useState<VwTestDetaybilgiDTO>(null);

    /**
     * Props da value değeri varsa ve query'den kayıt döndüyse value set edilir. Tek kayıt varsa value otomatik set edilir.
    */
    useEffect(() => {
        if (!query.isFetching && query.data?.length > 0) {
            if (query.data?.length === 1) {
                setValue(query.data[0]);
                if (props.onChange)
                    props.onChange({ target: { value: query.data?.at(0)?.id, name: props.name } });
                return;
            }
            if (props?.value) {
                setValue(query.data?.find((f) => f?.id === props?.value));
                if (props.onChange)
                    props.onChange({ target: { value: query.data?.find((f) => f?.id === props?.value)?.id, name: props.name } });
            } else {
                setValue(new VwTestDetaybilgiDTO());
                if (props.onChange)
                    props.onChange({ target: { value: null, name: props.name } });
            }
        }
    }, [query.isFetching, props.value]);

    /**
     * Kayıt seçildikten sonra inputta görünecek alan.
     * @param option 
     * @returns 
    */
    const getOptionLabel = (option: VwTestDetaybilgiDTO): string => {
        if (!option?.aciklama)
            return "";
        return option?.aciklama;
    };

    /**
     * Autocomplete textfield olarak görünür.
     * @param params 
     * @returns 
    */
    const renderInput = (params: AutocompleteRenderInputParams): React.ReactNode => {
        return (
            <TextField
                {...params}
                label={props.label}
                disabled={query.isFetching || props.disabled}
                error={Boolean(props.error)}
                helperText={props.helperText}
                required={props.required}
            />
        )
    };

    /**
     * Autocomplete listesinde lookup açılınca dönen datalar
     * @param props 
     * @param option 
     * @param state 
     * @param ownerState 
     * @returns 
    */
    const renderOption = (props, option: VwTestDetaybilgiDTO, state: AutocompleteRenderOptionState, ownerState: AutocompleteOwnerState<any, boolean, boolean, boolean, "div">): React.ReactNode => {
        if (!option.id)
            return "";
        return (
            <li {...props} key={option.id}>
                <div key={option.id}>
                    <b>{option.id}</b> - {option?.aciklama}
                </div>
            </li>
        )
    };

    /**
     * Değişiklik algılandığında onChange ile value set edilir.
     * @param event 
     * @param value 
     * @param reason 
     * @param details 
    */
    const onChange = (event: React.SyntheticEvent<Element, Event>, value: VwTestDetaybilgiDTO, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<any>) => {
        if (props.onChange)
            props.onChange({ target: { value: value?.id, name: props.name } });
    };

    /**
     * İnput içerisinde "id", "aciklama" ve "veri" alanı için yazılan değere göre arama yapar.
     * @param options 
     * @param state 
     * @returns 
    */
    const filterOptions = (options: VwTestDetaybilgiDTO[], state: FilterOptionsState<any>): VwTestDetaybilgiDTO[] => {
        return options?.filter((f) =>
            helper.objectToString(f.id).indexOf(state.inputValue) > -1 ||
            helper.UpperCaseAsc(f.aciklama).indexOf(helper.UpperCaseAsc(state.inputValue)) > -1 ||
            helper.UpperCaseAsc(f.veri).indexOf(helper.UpperCaseAsc(state.inputValue)) > -1
        );
    };

    /**
     * Seçeneklerdeki değerleri seçilen değer arasında eşleşmeyi id ile yapar ve ona göre true-false döner.
     * @param option 
     * @param value 
     * @returns 
    */
    const isOptionEqualToValue = (option: VwTestDetaybilgiDTO, value: VwTestDetaybilgiDTO): boolean => {
        return option?.id === value?.id;
    };

    return (
        <Autocomplete
            fullWidth
            sx={props?.sx}
            freeSolo={false}
            multiple={false}
            onChange={onChange}
            disabled={props.disabled}
            renderInput={renderInput}
            readOnly={props.readonly}
            loading={query.isFetching}
            id={props.id ? props.id : "test-detay-bilgi-autocomplete"}
            renderOption={renderOption}
            filterOptions={filterOptions}
            getOptionLabel={getOptionLabel}
            value={value?.id ? value : null}
            options={query.data ? query.data : []}
            isOptionEqualToValue={isOptionEqualToValue}
            loadingText="Aranıyor..."
        />
    )
};

export default TestDetayBilgiAutocomplete;